/**
 * Incomplete-tutors warning before validating a report.
 */
import * as React from 'react';
import { AlertTriangle } from 'lucide-react';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Button } from '@/components/forms/Button';
import {
  getBulkIncompleteTutorsModalDescription,
  getIncompleteTutorsModalDescription,
  shouldShowBulkReportValidationModal,
  shouldShowReportValidationModal,
  type ReportValidationOptions,
} from '@/utils/report-author-feedback-utils';
import type { SessionReport } from '@/types/inperson/report.types';

/** Props for {@link ReportValidationModal}. */
interface ReportValidationModalProps {
  open: boolean;
  onClose: () => void;
  /** Validate the report even if some tutors are incomplete. */
  onConfirm: () => void;
  /** Single-report validation. */
  report?: SessionReport | null;
  validateOptions?: ReportValidationOptions;
  /** Bulk validation (multi-select). */
  reports?: SessionReport[];
  validateOptionsByReportId?: Record<string, ReportValidationOptions>;
  isLoading?: boolean;
}

/** Confirm validation of one or many in-person reports. */
export const ReportValidationModal: React.FC<Readonly<ReportValidationModalProps>> = ({
  open,
  onClose,
  onConfirm,
  report,
  validateOptions,
  reports,
  validateOptionsByReportId,
  isLoading = false,
}) => {
  const [isProcessing, setIsProcessing] = React.useState(false);

  const isBulk = !!reports && reports.length > 0;
  const optionsMap =
    validateOptionsByReportId ??
    (report && validateOptions ? { [report.id]: validateOptions } : {});

  const shouldShow = isBulk
    ? shouldShowBulkReportValidationModal(reports, optionsMap)
    : !!report && shouldShowReportValidationModal(report, validateOptions);

  if (!open || !shouldShow) {
    return null;
  }

  let description: string | null = null;
  if (isBulk) {
    description = getBulkIncompleteTutorsModalDescription(reports, optionsMap);
  } else if (report) {
    description = getIncompleteTutorsModalDescription(report, validateOptions);
  }

  if (!description) {
    return null;
  }

  const paragraphs = description.split('\n\n').filter((p) => p.trim().length > 0);

  const handleConfirm = () => {
    setIsProcessing(true);
    try {
      onConfirm();
      onClose();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      size="md"
      className="report-validation-modal"
      hideContentWrapper
      header={{
        icon: AlertTriangle,
        iconColor: 'var(--color-3-nuance-1)',
        iconBgColor: 'color-mix(in srgb, var(--color-3-nuance-1) 15%, transparent)',
        iconSize: 'md',
        title: 'Compte rendu incomplet',
        titleColor: 'var(--foreground)',
        titleSize: 'lg',
        titleWeight: 'semibold',
      }}
      headerSeparatorClassName="report-validation-modal__header-rule"
      footerSeparatorClassName="report-validation-modal__footer-rule"
      footer={
        <div className="flex gap-2 justify-end w-full">
          <Button preset="modal-cancel" onClick={onClose} disabled={isProcessing || isLoading}>
            Annuler
          </Button>
          <Button
            preset="modal-confirm"
            onClick={handleConfirm}
            loading={isProcessing || isLoading}
            disabled={isProcessing || isLoading}
          >
            Valider quand même
          </Button>
        </div>
      }
    >
      <div className="report-validation-modal__body">
        {paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
    </BaseModal>
  );
};
